import { css, LinearProgress, Paper, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import React from 'react';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { MapBrowserEvent, MapEvent } from 'ol';

import { useAPI } from 'hooks/useAPI';
import { useSeismicStore } from '../stores/useSeismicStore';
import { useMapStore } from '../stores/useMapStore';
import { ApiType } from 'models/enums/ApiType';
import { getAmplitude, getTrace } from '../utils/calculateScreenValuesUtils';
import { getParamsFromUrl } from '../utils/seismicUrlUtils';
import { TraceHeader } from '../models/types/TraceHeader';
import { SeismicNavbarLoadingIcon } from './SeismicNavbarLoadingIcon';
import { useLine3DNavigationStore } from '../stores/useLine3DNavigationStore';
import { SurveyType } from '../models/enums/SurveyType';
import { getLineGeometryPerformanceDataFromAxiosResponse } from '../utils/performanceUtils';
import { useTraceHeadersForRange } from '../hooks/useTraceHeadersForRange';

import type { Range } from '@tanstack/react-virtual';

export function TraceHeaderTable() {
    const { surveyMetadata, traceRange, isInverted, seismicCalculator, scale, setTraceRange, setIsNavbarLoading, sortOption } = useSeismicStore(state => ({
        surveyMetadata: state.surveyMetadata,
        traceRange: state.rangeTrace,
        isInverted: state.isInverted,
        seismicCalculator: state.calculator,
        scale: state.scale,
        setTraceRange: state.setRangeTrace,
        setIsNavbarLoading: state.setNavbarLoading,
        sortOption: state.sortOption,
    }));

    const { lineType, lineNumber } = useLine3DNavigationStore(state => ({
        lineType: state.lineType,
        lineNumber: state.lineNumber
    }));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lastMouseMoveEventRef = useRef<MapBrowserEvent<any>>();

    const { current: urlParams } = useRef(getParamsFromUrl());
    const traceHeaderQueryRange = urlParams.traceHeaderQueryRange;
    const showSortOption = urlParams.showSortOption;

    const currentScrollRef = useRef<number>(0);

    const { map } = useMapStore(state => ({
        map: state.map
    }));
    const [lastTrace, setlastTrace] = useState(0);

    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    const [ maxTrace, minTrace, increment, sampleInterval, lineIncrement ] = useMemo(() =>
        !!surveyMetadata && !!seismicCalculator ?
            [
                seismicCalculator.getMaxTrace(surveyMetadata),
                seismicCalculator.getMinTrace(surveyMetadata),
                seismicCalculator.getLineIncrement(surveyMetadata),
                seismicCalculator.calculateSampleInterval(surveyMetadata),
                seismicCalculator.getLineIncrement(surveyMetadata)
            ]
            :
            [
                0, 1, 1, 1, 1
            ]
    , [seismicCalculator, surveyMetadata, lineType]);
    const theme = useTheme();

    const { traceHeaders, isFetching } = useTraceHeadersForRange({
        range: traceRange,
        surveyMetadata,
        increment,
        minTrace,
        maxTrace,
        queryRange: traceHeaderQueryRange,
        isInverted,
        lineNumber,
        lineType,
        sortOption,
        showSortOption,
    });

    const rowVirtualizer = useVirtualizer({
        count: traceHeaders.length,
        horizontal: true,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 150,
        rangeExtractor: React.useCallback(
            (range: Range) => {
                const stickyIndex = 0;

                const next = new Set([
                    stickyIndex,
                    ...defaultRangeExtractor(range),
                ]);

                const array : number[] = [];
                next.forEach(x => array.push(x));

                return [...array];
            },
            []
        ),
    });

    const styles = css({
        position: 'static',
        '& .row:first-child': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            position: 'sticky'
        },
        '.container-loading': {
            background: '#00000099',
            zIndex: 9,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: '1 1 auto',
            '& > *': {
                animation: 'fade 2s linear',
                animationIterationCount: 'infinite'
            }
        }
    });

    const scrollTo = useCallback((x: number, y: number) => {
        const index = traceHeaders.findIndex(traceHeader => traceHeader.TraceNumber === x);
        console.log('INDEX FOUND FOR HEADER LIST =====>', index);
        if (index >= 0) {
            const totalTraces = traceHeaders.length;
            const cellSize = rowVirtualizer.getTotalSize() / totalTraces;
            let elementsPerRow = 12;

            if (tableContainerRef && tableContainerRef.current) {
                elementsPerRow = tableContainerRef.current?.clientWidth / cellSize;
                const rowInitialPosition = cellSize;
                const middleRowOffset = (elementsPerRow / 2) * cellSize;
                const p = (index * rowInitialPosition - middleRowOffset);
                tableContainerRef.current?.scrollTo({ left: p });
            }
        }
    }, [increment, minTrace, rowVirtualizer, scale.x, traceHeaders]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mouseMoveCallback = useCallback((event: MapBrowserEvent<any>) => {
        if (!surveyMetadata || !seismicCalculator){
            return;
        }

        lastMouseMoveEventRef.current = event;

        setTimeout(() => {
            if (lastMouseMoveEventRef.current === event) {
                let [x , y] = [getTrace(event.coordinate[0], scale.x, minTrace, increment), parseFloat(getAmplitude(event.coordinate[1], scale.y, sampleInterval))];

                if (isInverted && !!surveyMetadata && !!seismicCalculator) {
                    x = maxTrace - x + minTrace;
                }

                setlastTrace(parseInt(x.toString()));
                scrollTo(x, y);
            }
        }, 100);
    }, [surveyMetadata, seismicCalculator, scale.x, scale.y, minTrace, increment, sampleInterval, isInverted, scrollTo, maxTrace]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mouseMoveCallbackRef = useRef<(event: MapBrowserEvent<any>) => void>();

    useEffect(() => {
        setIsNavbarLoading(isFetching);
    }, [isFetching]);

    useEffect(() => {
        if (map) {
            if (!!mouseMoveCallbackRef.current) {
                map.un('pointermove', mouseMoveCallbackRef.current);
            }
            map.on('pointermove', mouseMoveCallback);
            mouseMoveCallbackRef.current = mouseMoveCallback;
        }
    }, [mouseMoveCallback]);

    const rows = getRowsWithoutZero(traceHeaders, traceRange.initial, traceRange.end);

    const updateTraceRangeToLoad = useCallback((event: MapEvent) => {
        if (!surveyMetadata) {
            return;
        }

        const eventMap = event.map;
        if (!eventMap) {
            return;
        }
        const extent = eventMap.getView().calculateExtent(eventMap.getSize());
        let startTrace = getTrace(extent[0], scale.x, minTrace, increment);
        let endTrace = getTrace(extent[2], scale.x, minTrace, increment);

        if (startTrace < minTrace) {
            startTrace = minTrace;
        }
        if (endTrace > maxTrace) {
            endTrace = maxTrace;
        }

        if (startTrace >= traceRange.initial && endTrace <= traceRange.end) {
            return;
        }

        currentScrollRef.current = tableContainerRef.current?.scrollTop ?? 0;

        setTraceRange(
            Math.min(startTrace, traceRange.initial),
            Math.max(endTrace, traceRange.end),
        );
    }, [surveyMetadata, scale.x, minTrace, increment, maxTrace, traceRange.initial, traceRange.end, setTraceRange]);

    const updateTraceRangeToLoadRef = useRef(updateTraceRangeToLoad);

    useEffect(() => {
        if (!surveyMetadata || !map || !surveyMetadata.IsIndexed) {
            return;
        }

        map.un('moveend', updateTraceRangeToLoadRef.current);
        map.on('moveend', updateTraceRangeToLoad);
        updateTraceRangeToLoadRef.current = updateTraceRangeToLoad;
    }, [map, surveyMetadata, updateTraceRangeToLoad]);

    useEffect(() => {
        tableContainerRef.current?.scrollTo({ top: currentScrollRef.current });
    }, [traceHeaders]);

    useEffect(() => {
        if (!!map && !!surveyMetadata && (surveyMetadata.Type !== SurveyType.Seismic3D || lineNumber > 0) && surveyMetadata.IsIndexed) {
            const extent = map.getView().calculateExtent(map.getSize());
            let startTrace = getTrace(extent[0], scale.x, minTrace, increment);
            let endTrace = getTrace(extent[2], scale.x, minTrace, increment);

            if (startTrace < minTrace) {
                startTrace = minTrace;
            }
            if (endTrace > maxTrace) {
                endTrace = maxTrace;
            }

            setTraceRange(startTrace, endTrace);
        }
    }, [isInverted, surveyMetadata, lineType, lineNumber, sortOption]);

    return (
        <div css={styles}>
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <div
                    ref={tableContainerRef}
                    style={{
                        height: `400px`,
                        overflow: 'auto',
                    }}
                >
                    {isFetching || !rows ?
                        <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <LinearProgress/>
                            <SeismicNavbarLoadingIcon show={true} fontSize='large'/>
                        </div>
                        :
                        rows.map((x, index) =>
                            <div
                                className='row'
                                key={index}
                                style={{
                                    width: `${rowVirtualizer.getTotalSize()}px`,
                                    height: 20,
                                    position: (index === 0 ? 'sticky' : 'relative'),
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    padding: (index === 0 ? '10px 0' : '5px 0'),
                                    zIndex: (index === 0 ? 1 : 0),
                                    top: 0,
                                }}
                            >
                                {rowVirtualizer.getVirtualItems().map((virtualItem, i) => {
                                    const isSelected = (index === 0 && lastTrace.toString() === x[virtualItem.index]);
                                    return (
                                        <div
                                            className='cell'
                                            key={virtualItem.key}
                                            style={{
                                                width: `${virtualItem.size}px`,
                                                ...(
                                                    i === 0 ?
                                                        { position: 'sticky', zIndex: 1, backgroundColor: index === 0 ? theme.palette.primary.main : theme.palette.primary.contrastText, marginTop: '-10px'}
                                                        :
                                                        {  position: 'absolute', zIndex: 0, backgroundColor: isSelected ? theme.palette.secondary.main : '', transform: `translateX(${virtualItem.start}px)` }
                                                ),
                                                color: isSelected ? theme.palette.secondary.contrastText : 'inherit',
                                                top: 0,
                                                left: 0,
                                                height: '100%'
                                            }}
                                        >
                                            {<Typography paddingLeft={ i === 0 ? '10px' : 0} variant={index === 0 ? 'h6' : 'body1'}>{index === 0 && virtualItem.index === 0 ? 'Trace#' : x[virtualItem.index]}</Typography>}
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    }
                </div>

            </Paper>
        </div>);
}

function getRowsWithoutZero(data: TraceHeader[] | null, rangeTraceStart: number, rangeTraceEnd: number) {
    if (!data) {
        return;
    };
    const numberTraceArray = ['Trace#', ...Array.from({ length: data.length }, (_, i) => (data[i].TraceNumber).toString())];
    const rows: string[][] = [numberTraceArray];
    if (data && data.length > 0) {
        const keys = Object.keys(data[0].Headers);
        keys.forEach((key, idx) => {
            let row: string[] = [];
            row.push(key);
            data.forEach(trace => {
                row.push(trace.Headers[key]);
            });
            let zeroCount = 0;
            for (let i = 1; i < row.length; i++) {
                if (Number(row[i]) === 0) {
                    zeroCount++;
                }
            }
            if (zeroCount !== row.length - 1) {
                rows.push(row);
            }
        });
    }
    return rows;
}
